import { useCallback, useState } from 'react';
import { ContentNode } from '@amzn/awscat-pattern-library-service-model';

import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { log } from '../../loggers/Logger';
import { SaveWorkingCopyError } from '../../services/Errors';
import { removePage } from '../../services/PageOperations';
import { sectionIdForNode } from '../v2-common/ContentNodeExtensions';
import { useNavigateToSection } from '../v2-hooks/useUrlHash';

/** Presentation state for the "Delete page" confirmation modal on the Contents tab. */
export interface DeletePageButtonViewModel {
  readonly isModalVisible: boolean;
  /** True while the page deletion is being persisted. */
  readonly isDeleting: boolean;
  readonly openModal: () => void;
  readonly dismissModal: () => void;
  /** Persist the page deletion and close the modal. No-op when already deleting. */
  readonly confirmDelete: () => void;
}

/**
 * Drives the "Delete page" confirmation modal. Removes the current page
 * from the content tree via PageOperations.removePage, then stages and
 * flushes through ContentEditorService.
 */
export function useDeletePageButtonViewModel(currentPage: ContentNode): DeletePageButtonViewModel {
  const service = useContentEditorService();
  const navigateToSection = useNavigateToSection();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const openModal = useCallback(() => {
    setIsModalVisible(true);
  }, []);

  const dismissModal = useCallback(() => {
    setIsModalVisible(false);
    setIsDeleting(false);
  }, []);

  const confirmDelete = useCallback(() => {
    if (isDeleting) return;
    const content = service.content;
    if (!content) {
      setIsModalVisible(false);
      return;
    }
    const updated = removePage(content, currentPage.id);
    if (!updated) {
      setIsModalVisible(false);
      return;
    }
    setIsDeleting(true);
    void (async () => {
      try {
        // Repair the route before removing the page from the content tree. This keeps the
        // rendered page and URL in sync throughout the optimistic update.
        navigateToSection(sectionIdForNode(updated.contentData));
        service.stageEdit(updated);
        await service.flush();
      } catch (error) {
        // Only roll back when no newer edit replaced this deletion while the flush was
        // in flight. Restoring the page also makes its previous route valid again, and
        // re-enables the modal so the still-mounted component can retry.
        if (service.content === updated) {
          service.stageEdit(content);
          navigateToSection(sectionIdForNode(currentPage));
          setIsDeleting(false);
        }
        // Let the error boundary catch unexpected exceptions and report to RUM.
        if (!(error instanceof SaveWorkingCopyError)) throw error;
        log.error({ err: error }, 'Failed to save page deletion to working copy');
        // The flashbar persists across the re-render that would otherwise
        // unmount any inline alert in this modal, and gives the user a
        // retry path.
        FlashbarMessageBroker.create().pushErrorMessage(
          'There was an error deleting the page. Please reload the page and try again.',
          undefined,
          undefined,
          'Reload',
          () => window.location.reload(),
        );
      }
    })();
  }, [isDeleting, service, currentPage, navigateToSection]);

  return {
    isModalVisible,
    isDeleting,
    openModal,
    dismissModal,
    confirmDelete,
  };
}
